<template>
  <USelectMenu
    v-model="currentValue"
    :items="items"
    label-key="displayName"
    :by="compareItems"
    :placeholder="_placeholder"
    :disabled="isDisabled"
    :filter-fields="['displayName']"
    v-bind="$attrs"
  >
    <template #default="{ modelValue: selected }">
      <template v-if="isFetching && (isUnset || !selected)">
        <div class="flex items-center space-x-2">
          <o-loader :scale="0.6" class="h-3 w-3" />
          <p class="block text-gray-800 line-clamp-1 dark:text-gray-100">
            {{ $t('general.loading') }}
          </p>
        </div>
      </template>

      <template v-else-if="isNone">
        <p class="block text-gray-800 dark:text-gray-100">
          {{ $t('general.none') }}
        </p>
      </template>

      <template v-else-if="isFirstAvailable && allowAny">
        <p class="block text-gray-800 dark:text-gray-100">
          {{ $t('service.firstAvailable') }}
        </p>
      </template>

      <template v-else-if="selected && !isSpecialItem(selected)">
        <div class="w-full flex items-center justify-between pr-2">
          <div class="flex items-center">
            <o-avatar :items="[selected]" class="hidden md:flex pr-2" />
            <div>
              <p class="block text-gray-800 line-clamp-1 dark:text-gray-100">
                {{ selected.displayName }}
              </p>
              <p v-if="showEmail && selected.email" class="text-xs text-gray-500 line-clamp-1 dark:text-gray-400">
                {{ selected.email }}
              </p>
            </div>
          </div>

          <template v-if="availability">
            <o-chip v-if="!availability.includes(selected.idEmployee)" variant="warning" size="xs">
              {{ $t('components.employee.elements.select.unavailable') }}
            </o-chip>
          </template>
        </div>
      </template>
    </template>

    <template #item="{ item }">
      <div v-if="item" class="w-full flex items-center justify-between">
        <div class="flex items-center">
          <o-avatar v-if="!isSpecialItem(item)" :items="[item]" class="hidden md:flex pr-2 shrink-0" />
          <div>
            <p class="text-sm text-gray-700 line-clamp-1 dark:text-gray-200">
              {{ item.displayName }}
            </p>
            <p v-if="showEmail && item.email" class="text-xs text-gray-500 line-clamp-1 dark:text-gray-400">
              {{ item.email }}
            </p>
          </div>
        </div>

        <template v-if="availability && !isSpecialItem(item)">
          <o-chip v-if="availability.includes(item.idEmployee)" variant="success" size="xs">
            {{ $t('general.available') }}
          </o-chip>
        </template>
      </div>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

import {
  OAvatar,
  OChip,
  OLoader,
} from '@/components/elements'

import { EmployeeType } from '~/_api/models/employee'

import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  modelValue?: any
  types?: any[] | null
  filter?: Function | null
  disabled?: boolean
  allowAny?: boolean
  allowNone?: boolean
  fieldClass?: string
  placeholder?: string | null
  options?: any[] | null
  preselect?: boolean
  availabilityOptions?: Record<string, any> | null
  showEmail?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  types: null,
  filter: null,
  disabled: false,
  allowAny: false,
  allowNone: false,
  fieldClass: 'text-sm',
  placeholder: null,
  options: null,
  preselect: true,
  availabilityOptions: null,
  showEmail: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change'): void
}>()

const { $api } = useNuxtApp()
const { t } = useI18n()
const employeeStore = useEmployeeStore()

const originalValue = ref<any>(null)
const availableEmployees = ref<any>(null)
const isFetching = ref(false)

function toEmployeeArray(value: any): any[] {
  return Array.isArray(value) ? value : []
}

const noneItem = computed(() => ({
  _sentinel: -1,
  idEmployee: '__none__',
  displayName: t('general.none'),
}))

const firstAvailableItem = computed(() => ({
  _sentinel: 0,
  idEmployee: '__first_available__',
  displayName: t('service.firstAvailable'),
}))

function isSpecialItem(item: any): boolean {
  return item?._sentinel !== undefined
}

function compareItems(a: any, b: any): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (a._sentinel !== undefined && b._sentinel !== undefined) return a._sentinel === b._sentinel
  if (a.idEmployee && b.idEmployee) return a.idEmployee === b.idEmployee
  return false
}

const currentValue = computed({
  get() {
    const mv = props.modelValue
    if (mv === -1) return noneItem.value
    if (mv === 0) return firstAvailableItem.value
    return mv
  },
  set(value) {
    if (value && value._sentinel !== undefined) {
      emit('update:modelValue', value._sentinel)
    } else {
      emit('update:modelValue', value)
    }
    emit('change')
  },
})

const employees = computed(() => toEmployeeArray(employeeStore.all))

const filteredEmployees = computed(() => {
  let emps = toEmployeeArray(props.options ?? employees.value)

  if (props.types) {
    const typeMap: Record<string, EmployeeType> = {
      employee: EmployeeType.Employee,
      resource: EmployeeType.Resource,
      admin: EmployeeType.Admin,
    }
    const types = props.types.map((type: any) => typeMap[type] ?? type)
    emps = emps.filter((employee: any) => types.includes(employee.type))
  }

  if (props.filter) {
    emps = emps.filter(props.filter as any)
  }

  if (originalValue.value && originalValue.value.idEmployee) {
    const included = emps.find((employee: any) => employee.idEmployee === originalValue.value.idEmployee)

    if (!included) {
      emps = [...emps, originalValue.value]
    }
  }

  return emps
})

const items = computed(() => {
  const specialItems: any[] = []

  if (props.allowAny && !isMulti.value) {
    specialItems.push(firstAvailableItem.value)
  }

  if (props.allowNone && !isMulti.value) {
    specialItems.push(noneItem.value)
  }

  if (specialItems.length) {
    return [
      ...specialItems,
      { type: 'separator' as const },
      ...filteredEmployees.value,
    ]
  }

  return filteredEmployees.value
})

const employeeItems = computed(() => {
  return filteredEmployees.value.filter((item: any) => item?.idEmployee)
})

const availability = computed(() => {
  const avail = availableEmployees.value

  if (avail) {
    const service = props.availabilityOptions?.service
    const resource = props.availabilityOptions?.resource
    const emp = resource ? service.employee?.idEmployee : null

    const resources = service?.resources
      ?.filter((item: any) => {
        const isSameServiceResource = resource?.serviceResource?.idServiceResource === item.serviceResource?.idServiceResource
        const isSameResourceCount = resource?.count === item.count

        if (isSameServiceResource) {
          return !isSameResourceCount
        }

        return true
      })
      ?.map((item: any) => item.resource?.idEmployee) || []

    const used = [emp, ...resources]

    return avail
      .map((e: any) => e.idEmployee)
      .filter((idEmployee: any) => !used.includes(idEmployee))
  }

  return null
})

const _placeholder = computed(() => {
  if (props.placeholder) {
    return props.placeholder
  }

  switch ((props as any).type) {
    case 'employee':
      return t('employee.select')
    case 'resource':
      return t('resource.select')
    default:
      return t('components.employee.elements.select.selectItem')
  }
})

const isMulti = computed(() => {
  return Array.isArray(props.modelValue)
})

const isDisabled = computed(() => {
  return props.disabled || !filteredEmployees.value.length
})

const isNone = computed(() => {
  return props.modelValue === -1
})

const isFirstAvailable = computed(() => {
  return props.modelValue === 0
})

const isUnset = computed(() => {
  return props.modelValue === null
})

const _items = computed(() => {
  return cloneDeep(filteredEmployees.value)
})

const _availabilityOptions = computed(() => {
  return cloneDeep(props.availabilityOptions)
})

const fetchAvailability = debounce(async () => {
  isFetching.value = true

  const availOpts = props.availabilityOptions

  if (availOpts) {
    const { service, date, reservations, reservationCustomers, startTime } = availOpts

    if (service && date) {
      try {
        const emps = employeeItems.value

        const response = await $api.booking.availableEmployees({
          employeeIds: emps.map((e: any) => e.idEmployee),
          date,
          startTime,
          duration: service.duration,
          processing: service.processing,
          finishing: service.finishing,
          preBuffer: service.bufferTimePre ?? service.preBuffer ?? 0,
          postBuffer: service.bufferTimePost ?? service.postBuffer ?? 0,
          excludeReservationIds: reservations?.map((r: any) => r.idReservation) ?? [],
          excludeReservationCustomerIds: reservationCustomers?.map((rc: any) => rc.idReservationCustomer) ?? [],
        })
        const avail = response.items

        if (props.preselect && (isFirstAvailable.value || isUnset.value)) {
          emit('update:modelValue', avail[0] || 0)
          emit('change')
        } else if (props.allowNone && isUnset.value) {
          emit('update:modelValue', -1)
          emit('change')
        }

        availableEmployees.value = avail
        isFetching.value = false

        return
      } catch (e) {
        console.log(e)
      }
    }
  }

  availableEmployees.value = null
  isFetching.value = false
}, 500)

watch(_items, (newValue, oldValue) => {
  const oldIDs = oldValue.map((item: any) => item?.idEmployee)
  const newIDs = newValue.map((item: any) => item?.idEmployee)

  const changed = JSON.stringify(oldIDs) !== JSON.stringify(newIDs)

  if (changed) {
    fetchAvailability()
  }
})

watch(_availabilityOptions, (newValue, oldValue) => {
  if (!newValue || !oldValue) {
    fetchAvailability()
    return
  }

  const oldOptions = {
    date: oldValue.date,
    reservations: oldValue.reservations?.map((item: any) => item.idReservation),
    reservationCustomers: oldValue.reservationCustomers?.map((item: any) => item.idReservationCustomer),
    startTime: oldValue.startTime,
    duration: oldValue.service?.duration,
    processing: oldValue.service?.processing,
    finishing: oldValue.service?.finishing,
    preBuffer: oldValue.service?.bufferTimePre ?? oldValue.service?.preBuffer,
    postBuffer: oldValue.service?.bufferTimePost ?? oldValue.service?.postBuffer,
  }

  const newOptions = {
    date: newValue.date,
    reservations: newValue.reservations?.map((item: any) => item.idReservation),
    reservationCustomers: newValue.reservationCustomers?.map((item: any) => item.idReservationCustomer),
    startTime: newValue.startTime,
    duration: newValue.service?.duration,
    processing: newValue.service?.processing,
    finishing: newValue.service?.finishing,
    preBuffer: newValue.service?.bufferTimePre ?? newValue.service?.preBuffer,
    postBuffer: newValue.service?.bufferTimePost ?? newValue.service?.postBuffer,
  }

  const changed = JSON.stringify(oldOptions) !== JSON.stringify(newOptions)

  if (changed) {
    fetchAvailability()
  }
})

onMounted(() => {
  employeeStore.sync()
  originalValue.value = props.modelValue
  fetchAvailability()
})
</script>
